import { useState } from "react";
import { Download, FileSignature, Copy, Printer, Check } from "lucide-react";
import { toast } from "sonner";

/* ---------------------------------------------------------------- *
 * Static contract data
 * ---------------------------------------------------------------- */
const CONTRACT_FILES = {
  terms: {
    id: "terms",
    title: "Vendor Terms & Conditions",
    subtitle: "Standard terms every vendor must read and sign.",
    href: "/contracts/Vendor_Terms_and_Conditions.docx",
    filename: "Vendor_Terms_and_Conditions.docx",
  },
  contract: {
    id: "contract",
    title: "Vendor Contract",
    subtitle: "Full agreement governing the vendor's listing & services.",
    href: "/contracts/Vendor_Contract.docx",
    filename: "Vendor_Contract.docx",
  },
};

/* Plain text content rendered as a styled form on the dashboard.
 * Kept inline to avoid extra fetch round-trips. */
const TERMS_SECTIONS = [
  {
    h: "1. Introduction",
    body:
      "These Terms & Conditions (\u201CTerms\u201D) govern the relationship between the vendor (\u201CVendor\u201D, \u201CYou\u201D) and the directory platform (\u201CWe\u201D, \u201CUs\u201D, \u201CThe Platform\u201D). By creating an account and listing your business, you agree to comply with these Terms.",
  },
  {
    h: "2. Vendor Eligibility",
    list: [
      "You must be a legitimate business operating within the wedding or events industry",
      "You must provide accurate, up to date business information",
      "You must have the authority to represent and promote the business you are listing",
    ],
  },
  {
    h: "3. Vendor Account & Listing",
    list: [
      "You are responsible for maintaining accurate business details, images, pricing, and contact information",
      "You must not upload misleading, false, or plagiarized content",
      "You must ensure all images and media you upload are owned by you or licensed for use",
      "We reserve the right to edit, remove, or decline listings that do not meet our quality or compliance standards",
    ],
  },
  {
    h: "4. Payments & Subscription Plans",
    list: [
      "Vendors may be required to pay subscription fees depending on the chosen plan",
      "All payments are non refundable unless otherwise stated",
      "Failure to make payment may result in suspension or removal of your listing",
    ],
  },
  {
    h: "5. Vendor Conduct",
    list: [
      "You agree to communicate professionally with couples and other vendors",
      "You must not engage in harassment, discrimination, or unethical business practices",
      "You must honor bookings, pricing, and agreements made with couples",
    ],
  },
  {
    h: "6. Reviews & Ratings",
    list: [
      "Couples may leave reviews based on their experience",
      "You must not attempt to manipulate, fake, or incentivise reviews",
      "We reserve the right to remove reviews that violate our guidelines",
    ],
  },
  {
    h: "7. Content Ownership & Licensing",
    list: [
      "You retain ownership of the content you upload",
      "By uploading content, you grant us a licence to display, promote, and distribute it on the platform",
      "You confirm that all uploaded content does not infringe on third party rights",
    ],
  },
  {
    h: "8. Platform Responsibilities",
    list: [
      "We provide a space for vendors to showcase their services but do not guarantee bookings",
      "We are not responsible for disputes between vendors and couples",
      "We may update or modify the platform at any time",
    ],
  },
  {
    h: "9. Vendor Responsibilities",
    list: [
      "You are solely responsible for the services you provide to couples",
      "You must comply with all relevant UK laws, licences, and insurance requirements",
      "You must ensure your business practices are safe, legal, and transparent",
    ],
  },
  {
    h: "10. Suspension & Termination",
    list: [
      "We may suspend or terminate your account for breach of these Terms",
      "We may remove content that violates our policies or harms the platform\u2019s reputation",
      "You may request account deletion at any time",
    ],
  },
  {
    h: "11. Liability",
    list: [
      "We are not liable for loss of business, revenue, or reputation resulting from your use of the platform",
      "We do not guarantee uninterrupted access to the platform",
      "Vendors are responsible for their own contracts, payments, and agreements with couples",
    ],
  },
  {
    h: "12. Data Protection & Privacy",
    list: [
      "You must handle customer data responsibly and in compliance with UK GDPR",
      "We process vendor and user data according to our Privacy Policy",
    ],
  },
  {
    h: "13. Changes to Terms",
    list: [
      "We may update these Terms at any time",
      "Continued use of the platform indicates acceptance of updated Terms",
    ],
  },
  {
    h: "14. Governing Law",
    body: "These Terms are governed by the laws of England and Wales.",
  },
];

const CONTRACT_SECTIONS = [
  {
    h: "Parties & Scope",
    body:
      "Parties: Platform (the directory operator) and Vendor (the business listing on the Platform). Scope: This Agreement governs the Vendor\u2019s use of the Platform, the Vendor\u2019s listing, and the relationship between the Vendor, couples, and the Platform.",
  },
  {
    h: "1. Definitions and Interpretation",
    body:
      "\u201CAgreement\u201D means this Vendor Contract and any schedules or annexes. \u201CPlatform\u201D means the directory operator and its affiliates, employees, agents and contractors. \u201CVendor\u201D means the business or individual who registers, lists or uses the Platform to offer services. \u201CListing\u201D means the Vendor profile, images, descriptions, pricing, and any content uploaded to the Platform. \u201CCouple\u201D means a user of the Platform seeking wedding or event services.",
  },
  {
    h: "2. Term and Scope of Services",
    body:
      "Effective date: This Agreement takes effect when the Vendor accepts it by creating a listing or otherwise using the Platform. Services provided by Platform: the Platform provides a listing service, marketing, and enquiry forwarding. The Platform does not provide vendor services, guarantee bookings, or act as agent for the Vendor unless a separate written agreement exists. Vendor services: the Vendor is solely responsible for the provision, quality, safety and legality of services offered to couples.",
  },
  {
    h: "3. Vendor Representations and Warranties",
    list: [
      "Legal status: it is a legally registered business and has authority to enter this Agreement.",
      "Accuracy: all Listing information is true, accurate and not misleading.",
      "Rights: it owns or has valid licences for all content uploaded and has obtained all necessary consents from persons depicted.",
      "Compliance: it will comply with all applicable laws, regulations, licences, permits and insurance requirements (including UK GDPR).",
      "No conflicts: listing and performance do not breach any other contract or third party rights.",
      "Insurance: it maintains appropriate public liability, professional indemnity and employer\u2019s liability insurance and will provide proof on request.",
    ],
  },
  {
    h: "4. Fees, Payment and Refunds",
    body:
      "Fees: Vendor agrees to pay the Platform fees for the chosen subscription or services as published. Please note that the listing is free for the first year. Payment terms: payments are due in accordance with the Platform\u2019s billing schedule. Late payment: overdue amounts for over 2 weeks can lead to the profile being suspended until payment is made. Refunds: fees are non refundable except where the Platform expressly agrees in writing. Taxes: Vendor is responsible for all taxes arising from its business and services.",
  },
  {
    h: "5. Listing Content, Quality Control and Platform Rights",
    body:
      "Content standards: Vendor must ensure Listing content meets the Platform\u2019s quality, format and content guidelines. The Platform may edit Listing content for formatting, clarity or compliance. The Platform may suspend, restrict, remove or terminate a Listing or Vendor account where the Vendor breaches this Agreement, provides false content, engages in harassment, fraud or criminal activity, fails to maintain required insurance or licences, or poses an immediate risk to users or the Platform\u2019s reputation.",
  },
  {
    h: "6. Vendor Obligations and Conduct",
    body:
      "Professional conduct: Vendor will act professionally, respond to enquiries promptly, and honour confirmed bookings and agreed terms with couples. Transparency: Vendor will disclose material limitations, restrictions, or additional fees before confirming bookings. Data handling: Vendor will process personal data only for legitimate business purposes, in compliance with UK GDPR. Subcontracting: Vendor remains fully liable for subcontractor performance. Record keeping: Vendor will retain records of bookings, contracts and communications for at least six years.",
  },
  {
    h: "7. Intellectual Property",
    body:
      "Vendor retains ownership of its pre existing IP and content uploaded. Vendor grants the Platform a perpetual, worldwide, royalty free, non exclusive licence to use, reproduce, display, distribute and promote Listing content on the Platform and related marketing channels. Platform retains all rights in its trademarks, software, designs and content.",
  },
  {
    h: "8. Confidentiality and Publicity",
    body:
      "Each party will keep confidential business information disclosed by the other and not disclose it except to employees, advisors or subcontractors who need to know and are bound by confidentiality. Vendor may not use the Platform\u2019s name or logo in marketing without prior written consent.",
  },
  {
    h: "9. Indemnity and Insurance",
    body:
      "Vendor indemnifies and holds the Platform harmless from all claims, losses, liabilities, damages, costs and expenses arising from Vendor\u2019s breach of this Agreement, negligence, wilful misconduct, or failure to perform services. Vendor will maintain and, upon request, provide evidence of adequate insurance covering public liability, professional indemnity and employer\u2019s liability where applicable.",
  },
  {
    h: "10. Limitation of Liability",
    body:
      "Neither party is liable for indirect, special, consequential or punitive losses. The Platform\u2019s aggregate liability to the Vendor for all claims arising under this Agreement will not exceed the total fees paid by the Vendor to the Platform in the 12 months preceding the claim. The Platform does not guarantee bookings, enquiries, or revenue.",
  },
  {
    h: "11. Termination and Post Termination",
    body:
      "Termination by Vendor: Vendor may terminate by deleting its account and providing 30 days\u2019 written notice for any paid subscription. Termination by Platform: Platform may terminate for breach, insolvency, illegal activity, or repeated poor performance. Effects of termination: upon termination Vendor\u2019s Listing will be removed.",
  },
  {
    h: "12. Dispute Resolution and Governing Law",
    body:
      "Good faith negotiation: parties will attempt to resolve disputes by senior representatives within 30 days. If unresolved, parties will attempt mediation in England and Wales. Jurisdiction: disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.",
  },
  {
    h: "13. Data Protection",
    body:
      "The Platform is the data controller for Platform operations; Vendor is controller for its own client data. Vendor will notify the Platform within 48 hours of any personal data breach affecting Platform users.",
  },
  {
    h: "14. Audit, Compliance and Right to Verify",
    body:
      "The Platform may, on reasonable notice and not more than once per year, audit Vendor records to verify compliance with this Agreement. Vendor will cooperate and provide requested documentation.",
  },
  {
    h: "15. Miscellaneous",
    body:
      "Entire agreement: this Agreement, the Vendor Code of Conduct, Privacy Policy and any schedules constitute the entire agreement. Variation must be in writing and signed by both parties. Vendor may not assign rights without Platform\u2019s prior written consent. Notices must be in writing.",
  },
  {
    h: "16. Recommendations and Legal Review",
    body:
      "This Agreement is drafted to be comprehensive, but it does not constitute legal advice. We strongly recommend that the Platform and Vendors obtain independent legal advice. By creating a Listing or using the Platform, the Vendor acknowledges having read, understood and agreed to be bound by this Agreement.",
  },
];

/* ---------------------------------------------------------------- *
 * Sub-components
 * ---------------------------------------------------------------- */
const SectionBlock = ({ section }) => (
  <div className="mb-6 last:mb-0">
    <h4 className="font-serif-display text-[18px] text-[var(--ld-ink)] mb-2">
      {section.h}
    </h4>
    {section.body && (
      <p className="text-[14px] leading-[1.75] text-[var(--ld-ink)]/85">
        {section.body}
      </p>
    )}
    {section.list && (
      <ul className="mt-2 space-y-1.5 list-disc pl-5 text-[14px] leading-[1.75] text-[var(--ld-ink)]/85">
        {section.list.map((li, i) => (
          <li key={`${i}-${(li || '').slice(0, 24)}`}>{li}</li>
        ))}
      </ul>
    )}
  </div>
);

const SignatureBlock = ({ form, setForm, includeDate, testidPrefix }) => (
  <div className="mt-8 grid sm:grid-cols-2 gap-5 border-t border-[var(--ld-border)] pt-6 print:break-inside-avoid">
    <label className="block">
      <span className="block font-accent text-[10px] tracking-[0.25em] uppercase text-[var(--ld-ink-muted)] mb-2">
        Name
      </span>
      <input
        data-testid={`${testidPrefix}-name`}
        type="text"
        value={form.name}
        onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
        className="w-full h-11 bg-transparent border-b border-[var(--ld-ink)]/40 px-1 text-[15px] text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)] print:border-b-2 print:border-[#000]"
        placeholder="Vendor full name"
      />
    </label>
    <label className="block">
      <span className="block font-accent text-[10px] tracking-[0.25em] uppercase text-[var(--ld-ink-muted)] mb-2">
        Signature
      </span>
      <input
        data-testid={`${testidPrefix}-signature`}
        type="text"
        value={form.signature}
        onChange={(e) => setForm((p) => ({ ...p, signature: e.target.value }))}
        className="w-full h-11 bg-transparent border-b border-[var(--ld-ink)]/40 px-1 text-[15px] italic font-serif-display text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)] print:border-b-2 print:border-[#000]"
        placeholder="Sign here"
      />
    </label>
    {includeDate && (
      <label className="block">
        <span className="block font-accent text-[10px] tracking-[0.25em] uppercase text-[var(--ld-ink-muted)] mb-2">
          Date
        </span>
        <input
          data-testid={`${testidPrefix}-date`}
          type="date"
          value={form.date}
          onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
          className="w-full h-11 bg-transparent border-b border-[var(--ld-ink)]/40 px-1 text-[15px] text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)] print:border-b-2 print:border-[#000]"
        />
      </label>
    )}
  </div>
);

/* ---------------------------------------------------------------- *
 * Main Panel
 * ---------------------------------------------------------------- */
const ContractsPanel = () => {
  const [tab, setTab] = useState("terms"); // terms | contract
  const [termsForm, setTermsForm] = useState({
    name: "",
    signature: "",
    date: new Date().toISOString().slice(0, 10),
  });
  const [contractForm, setContractForm] = useState({
    name: "",
    signature: "",
    date: new Date().toISOString().slice(0, 10),
  });
  const [copied, setCopied] = useState(null);

  const copyLink = async (file) => {
    const url = `${window.location.origin}${file.href}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(file.id);
      toast(`${file.title} link copied`, { duration: 2000 });
      setTimeout(() => setCopied(null), 1800);
    } catch (e) {
      toast("Could not copy link", { duration: 2000 });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const active = CONTRACT_FILES[tab];
  const isTerms = tab === "terms";

  return (
    <div data-testid="section-contracts">
      <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
        Paperwork
      </span>
      <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
        Contracts
      </h2>
      <p className="mt-2 text-sm text-[var(--ld-ink-muted)] max-w-xl">
        Standard agreements you can review, fill, print, or share. Originals are
        kept as downloadable Word documents below.
      </p>

      {/* Download cards */}
      <div
        data-testid="contracts-download-cards"
        className="mt-8 grid md:grid-cols-2 gap-4 print:hidden"
      >
        {Object.values(CONTRACT_FILES).map((file) => (
          <div
            key={file.id}
            data-testid={`contract-card-${file.id}`}
            className="border border-[var(--ld-border)] p-5 bg-[var(--ld-ivory)]/40 flex items-start gap-4"
          >
            <span className="inline-flex items-center justify-center h-11 w-11 bg-[var(--ld-ink)] text-[var(--ld-gold)] flex-shrink-0">
              <FileSignature size={18} strokeWidth={1.5} />
            </span>
            <div className="flex-1 min-w-0">
              <h3 className="font-serif-display text-[18px] text-[var(--ld-ink)]">
                {file.title}
              </h3>
              <p className="mt-1 text-[12px] text-[var(--ld-ink-muted)] leading-[1.6]">
                {file.subtitle}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <a
                  data-testid={`contract-download-${file.id}`}
                  href={file.href}
                  download={file.filename}
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[var(--ld-ink)] px-3 py-1.5 hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors"
                >
                  <Download size={13} strokeWidth={1.6} />
                  Download .docx
                </a>
                <button
                  data-testid={`contract-copy-${file.id}`}
                  onClick={() => copyLink(file)}
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/30 px-3 py-1.5 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
                >
                  {copied === file.id ? (
                    <>
                      <Check size={13} strokeWidth={1.8} />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy size={13} strokeWidth={1.6} />
                      Copy link
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="mt-10 flex items-center gap-1 border-b border-[var(--ld-border)] print:hidden">
        {Object.values(CONTRACT_FILES).map((file) => {
          const isActive = tab === file.id;
          return (
            <button
              key={file.id}
              data-testid={`contract-tab-${file.id}`}
              onClick={() => setTab(file.id)}
              className={`px-4 py-3 text-[11px] tracking-[0.22em] uppercase border-b-2 -mb-px transition-colors ${
                isActive
                  ? "border-[var(--ld-gold-deep)] text-[var(--ld-ink)]"
                  : "border-transparent text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]"
              }`}
            >
              {file.title}
            </button>
          );
        })}
        <div className="ml-auto pb-2">
          <button
            data-testid="contract-print"
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/30 px-3 py-1.5 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
          >
            <Printer size={13} strokeWidth={1.6} />
            Print / Save PDF
          </button>
        </div>
      </div>

      {/* Form rendering of the active contract */}
      <div
        id="contract-printable"
        data-testid={`contract-form-${active.id}`}
        className="mt-8 bg-white border border-[var(--ld-border)] p-8 sm:p-12 max-w-4xl print:border-0 print:p-0 print:max-w-none"
      >
        <div className="text-center border-b border-[var(--ld-border)] pb-6 mb-8">
          <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-[var(--ld-gold-deep)]">
            Luxé Directory
          </p>
          <h3 className="font-serif-display text-3xl text-[var(--ld-ink)] mt-3">
            {active.title}
          </h3>
        </div>

        {(isTerms ? TERMS_SECTIONS : CONTRACT_SECTIONS).map((section, i) => (
          <SectionBlock key={section.title || i} section={section} />
        ))}

        {isTerms ? (
          <>
            <p className="mt-8 text-[14px] text-[var(--ld-ink)] italic">
              Please sign here to state that you have read and understood the
              terms. I agree:
            </p>
            <SignatureBlock
              form={termsForm}
              setForm={setTermsForm}
              includeDate={true}
              testidPrefix="terms-sign"
            />
          </>
        ) : (
          <SignatureBlock
            form={contractForm}
            setForm={setContractForm}
            includeDate={true}
            testidPrefix="contract-sign"
          />
        )}
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #contract-printable, #contract-printable * { visibility: visible; }
          #contract-printable {
            position: absolute;
            left: 0; top: 0;
            width: 100%;
            padding: 24px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ContractsPanel;
